import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cameraConstraints,
  clampZoom,
  isCameraAvailable,
  nextDeviceId,
  readZoomRange,
  videoInputs,
  zoomConstraints,
  type ZoomRange,
} from './constraints';

export interface CameraZoom extends ZoomRange {
  value: number;
}

export interface UseCameraResult {
  videoRef: React.RefObject<HTMLVideoElement>;
  ready: boolean;
  error: string | null;
  capturePhoto: () => Promise<Blob | null>;
  stop: () => void;
  /** True when the device reports more than one camera. */
  canFlip: boolean;
  /** Moves to the next camera, wrapping at the end. */
  flip: () => void;
  /** Present only while the live track reports a `zoom` capability. */
  zoom: CameraZoom | null;
  setZoom: (value: number) => void;
}

/**
 * The live stream behind the viewfinder and rapid session
 * (CAPTURE_JOBS.md section 6). Opens the rear camera first, offers a flip
 * across every camera the device reports, and a zoom where the track
 * exposes one. The rules live in `constraints.ts`; this hook only owns the
 * stream's lifetime.
 *
 * `enabled` decides whether a stream is requested at all. A desktop capture
 * screen leaves it false until the operator asks for the camera, because
 * `getUserMedia` raises the browser permission prompt the moment it is
 * called.
 */
export function useCamera({ enabled = true }: { enabled?: boolean } = {}): UseCameraResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const currentDeviceRef = useRef<string | undefined>(undefined);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [inputs, setInputs] = useState<{ deviceId: string; label: string }[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoomState] = useState<CameraZoom | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    async function start() {
      if (!isCameraAvailable()) {
        setError('Camera access is unavailable on this device or browser.');
        return;
      }
      setError(null);

      try {
        const stream = await navigator.mediaDevices.getUserMedia(cameraConstraints(deviceId));
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const [track] = stream.getVideoTracks();
        currentDeviceRef.current = track?.getSettings?.().deviceId ?? deviceId ?? undefined;

        const range = readZoomRange(track);
        const current = (track?.getSettings?.() as { zoom?: number } | undefined)?.zoom;
        setZoomState(range ? { ...range, value: clampZoom(range, current ?? range.min) } : null);

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setReady(true);

        // Device ids are only readable once access is granted, so the list
        // is read after the stream opens rather than before.
        if (typeof navigator.mediaDevices.enumerateDevices === 'function') {
          const devices = await navigator.mediaDevices.enumerateDevices();
          if (!cancelled) setInputs(videoInputs(devices));
        }
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Camera access was denied.');
      }
    }

    void start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setReady(false);
    };
  }, [enabled, deviceId]);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setReady(false);
  }, []);

  const flip = useCallback(() => {
    const next = nextDeviceId(inputs, currentDeviceRef.current);
    if (next) setDeviceId(next);
  }, [inputs]);

  const setZoom = useCallback(
    (value: number) => {
      const track = streamRef.current?.getVideoTracks()[0];
      if (!track || !zoom) return;
      const clamped = clampZoom(zoom, value);
      setZoomState({ ...zoom, value: clamped });
      void track.applyConstraints(zoomConstraints(clamped)).catch(() => {
        // A device that refuses a value keeps its last zoom. The slider has
        // already moved, and the next frame shows what the lens did.
      });
    },
    [zoom],
  );

  const capturePhoto = useCallback(async (): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video || !ready) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92));
  }, [ready]);

  return { videoRef, ready, error, capturePhoto, stop, canFlip: inputs.length > 1, flip, zoom, setZoom };
}
